import React, { useState } from 'react';
import useInView from '../hooks/useInView';
import { LOANS, CONTACT_INFO, IMAGES } from '../data';

const ContactSection = ({ dark, t }) => {
  const [ref, inView] = useInView(0.1);
  const [form, setForm] = useState({ name: '', phone: '', email: '', loanType: '', amount: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); setSubmitted(true); }, 1800);
  };

  const inputStyle = {
    width: '100%', padding: '14px 18px', borderRadius: 10, boxSizing: 'border-box',
    border: `1.5px solid ${t.border}`,
    background: dark ? '#0e0e0e' : '#fff',
    color: t.text,
    fontFamily: "'DM Sans',sans-serif", fontSize: 15,
    outline: 'none', transition: 'border-color 0.3s',
  };

  const fields = [
    { key: 'name',   label: 'Full Name *',         type: 'text',   ph: 'Your full name' },
    { key: 'phone',  label: 'Phone Number *',       type: 'tel',    ph: '+91 9634629999 || +91 9953252454' },
    { key: 'email',  label: 'Email Address',        type: 'email',  ph: 'your@email.com' },
    { key: 'amount', label: 'Loan Amount (₹)',      type: 'number', ph: 'e.g. 5,00,000' },
  ];

  return (
    <section id="contact" style={{ padding: '120px 32px', background: t.bg2, position: 'relative', overflow: 'hidden' }}>
      <div ref={ref} style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }}>

          {/* ── Left Info ── */}
          <div
            style={{
              opacity: inView ? 1 : 0,
              animation: inView ? 'slideRight 0.8s 0.2s cubic-bezier(.16,1,.3,1) forwards' : 'none',
            }}
          >
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: t.gold, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 16, fontWeight: 600 }}>
              — Get In Touch —
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 'clamp(2rem,4vw,3rem)', color: t.text, marginBottom: 20 }}>
              Ready to Start<br />Your <span className="gold-text">Loan Journey?</span>
            </h2>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 16, color: t.textSub, lineHeight: 1.75, marginBottom: 40 }}>
              Our financial experts are ready to find you the best deal. Free consultation, zero hidden charges, complete transparency.
            </p>

            {/* Coverage image */}
            <div style={{ position: 'relative', marginBottom: 40, borderRadius: 16, overflow: 'hidden' }}>
              <img src={IMAGES.village} alt="We serve across India" style={{ width: '100%', height: 200, objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right,rgba(0,0,0,0.55),transparent)' }} />
              <div style={{ position: 'absolute', bottom: 18, left: 22, color: '#fff' }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 19, fontWeight: 700 }}>Covering All India</div>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, opacity: 0.8 }}>Villages · Cities · States</div>
              </div>
            </div>

            {/* Contact details */}
            {CONTACT_INFO.map((c) => (
              <div key={c.label} style={{ display: 'flex', gap: 16, marginBottom: 22, alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                    background: t.goldDim, border: `1px solid ${t.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                  }}
                >
                  {c.icon}
                </div>
                <div>
                  <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: t.gold, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2, fontWeight: 600 }}>
                    {c.label}
                  </div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 17, fontWeight: 600, color: t.text }}>
                    {c.val}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Right Form ── */}
          <div
            style={{
              background: t.surface,
              border: `1px solid ${t.border}`,
              borderRadius: 24,
              overflow: 'hidden',
              boxShadow: dark ? '0 32px 80px rgba(0,0,0,0.4)' : '0 32px 80px rgba(0,0,0,0.08)',
              opacity: inView ? 1 : 0,
              animation: inView ? 'slideLeft 0.8s 0.3s cubic-bezier(.16,1,.3,1) forwards' : 'none',
            }}
          >
            {/* Gold top bar */}
            <div style={{ height: 4, background: 'linear-gradient(90deg,#c9a227,#e8c040,#c9a227)' }} />

            <div style={{ padding: 44 }}>
              {submitted ? (
                /* Success state */
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <div style={{ fontSize: 68, marginBottom: 20, animation: 'float 2s ease-in-out infinite' }}>✅</div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 28, fontWeight: 700, color: t.text, marginBottom: 12 }}>
                    Application Received!
                  </h3>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 16, color: t.textSub, lineHeight: 1.7, marginBottom: 24 }}>
                    Our team will contact you within <strong style={{ color: t.gold }}>24 hours</strong> with the best loan options tailored for you.
                  </p>
                  <div style={{ padding: '18px', background: t.goldDim, borderRadius: 12, border: `1px solid ${t.border}` }}>
                    <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: t.textSub, marginBottom: 4 }}>Your reference number</div>
                    <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, fontWeight: 700, color: t.gold }}>
                      FFF-{Date.now().toString().slice(-6)}
                    </div>
                  </div>
                </div>
              ) : (
                /* Form */
                <form onSubmit={handleSubmit}>
                  <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 700, color: t.text, margin: '0 0 6px' }}>
                    Loan Application
                  </h3>
                  <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: t.textMuted, margin: '0 0 28px' }}>
                    Free consultation · No hidden charges
                  </p>

                  {fields.map((f) => (
                    <div key={f.key} style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: t.textSub, marginBottom: 6, fontWeight: 500 }}>
                        {f.label}
                      </label>
                      <input
                        required={f.label.includes('*')}
                        type={f.type}
                        placeholder={f.ph}
                        value={form[f.key]}
                        onChange={(e) => updateField(f.key, e.target.value)}
                        style={inputStyle}
                        onFocus={(e) => (e.target.style.borderColor = t.gold)}
                        onBlur={(e) => (e.target.style.borderColor = t.border)}
                      />
                    </div>
                  ))}

                  {/* Loan Type */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: t.textSub, marginBottom: 6, fontWeight: 500 }}>
                      Loan Type *
                    </label>
                    <select
                      required
                      value={form.loanType}
                      onChange={(e) => updateField('loanType', e.target.value)}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = t.gold)}
                      onBlur={(e) => (e.target.style.borderColor = t.border)}
                    >
                      <option value="">Select Loan Type</option>
                      {LOANS.map((l) => <option key={l.title}>{l.title}</option>)}
                      <option>Investment Advisory</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div style={{ marginBottom: 24 }}>
                    <label style={{ display: 'block', fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: t.textSub, marginBottom: 6, fontWeight: 500 }}>
                      Message (Optional)
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Any specific requirements..."
                      value={form.message}
                      onChange={(e) => updateField('message', e.target.value)}
                      style={{ ...inputStyle, resize: 'vertical' }}
                      onFocus={(e) => (e.target.style.borderColor = t.gold)}
                      onBlur={(e) => (e.target.style.borderColor = t.border)}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%', padding: '17px', borderRadius: 10, border: 'none',
                      background: loading ? '#888' : 'linear-gradient(135deg,#c9a227,#e8c040)',
                      color: '#0a0a0a',
                      fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 17,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      boxShadow: '0 8px 28px rgba(201,162,39,0.4)',
                      transition: 'all 0.3s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 12px 36px rgba(201,162,39,0.55)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = '';
                      e.currentTarget.style.boxShadow = '0 8px 28px rgba(201,162,39,0.4)';
                    }}
                  >
                    {loading ? (
                      <>
                        <span
                          style={{
                            width: 18, height: 18,
                            border: '2px solid #0a0a0a',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'spin 0.7s linear infinite',
                          }}
                        />
                        Processing...
                      </>
                    ) : (
                      'Submit Application →'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
